/* eslint-disable react/no-unused-prop-types */
import { Box } from '@mui/material';
import CustomSearchBox from 'components/app-container/custom-search-box/CustomSearchBox';
import UPButton from 'components/button';
import { UPDropdown } from 'components/dropdown';
import { RowSeverity, UPTanStackTable, UPTanStackTableColumn } from 'components/tanstack-table';
import { CoefficientsPerWorkerClassOptions } from 'constants/CoefficientsPerWorkerclass';
import { CompanyRole } from 'constants/CompanyRole';
import { useGridPagination } from 'hooks/useGridPagination';
import { useGridSearch } from 'hooks/useGridSearch';
import { toNumber } from 'lodash';
import { useCallback, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAppSelector } from 'store';
import { getParitairComiteSelectItemsByCompanyAction } from 'store/actions/paritair-comite-actions';
import { paritairComiteByCompanySelectItemsSelector } from 'store/selectors/paritair-comite-selectors';
import { profileSelector } from 'store/selectors/profile-selectors';
import {
  COMPANY_PAYROLL__ADD_NEW_WORKCLASS_EXEMPTION,
  COMPANY_PAYROLL__PARITAIR_COMITE,
  COMPANY_PAYROLL__WAGE_EXEMPTION,
  COMPANY_PAYROLL__WORKERCLASS,
  DATAGRID__NO_ROWS,
  GENERAL__SELECT,
  WAGE_INDEXATION__VALID_FROM,
  WAGE_INDEXATION__VALID_TO,
} from 'translations/constants';
import IWorkerclassExemption from 'types/company/payroll/IWorkerclassExemption';
import { WorkerclassExemptionsGridProps, WageExemptionField } from '../types';
import { DeleteActionCell, WageInputCell } from './cells';
import { isUnsavedWageExemptionRow } from './helpers';

const WorkerclassExemptionsGrid = (props: WorkerclassExemptionsGridProps) => {
  const { workerclassExemptions, setWorkerclassExemptions, rowErrors } = props;

  const [t] = useTranslation();
  const dispatch = useDispatch();

  const userIsCompanyManager = useAppSelector(profileSelector).role === CompanyRole.COMPANY_MANAGER;
  const paritairComites = useAppSelector(paritairComiteByCompanySelectItemsSelector);
  const { companyId } = useParams();
  const workerclassOptions = useMemo(() => CoefficientsPerWorkerClassOptions(), []);

  const matches = useCallback(
    (row: IWorkerclassExemption, term: string) => {
      const needle = term.toLowerCase();
      const workerclassLabel = workerclassOptions.find(o => o.value === row.workerclass)?.label ?? '';
      const paritairLabel = paritairComites?.find(p => p.value === row.paritairComiteId)?.label ?? '';
      const startDate = (row.startDate ?? '').toString();
      const endDate = (row.endDate ?? '').toString();
      return (
        workerclassLabel.toLowerCase().includes(needle) ||
        paritairLabel.toLowerCase().includes(needle) ||
        (row.wageExemption?.toString() ?? '').toLowerCase().includes(needle) ||
        startDate.toLowerCase().includes(needle) ||
        endDate.toLowerCase().includes(needle)
      );
    },
    [workerclassOptions, paritairComites],
  );

  // A row the user has just added is still blank, so it matches no search term. Exempting unsaved
  // rows keeps it in the grid it was added to instead of filtering it straight back out.
  const isUnsavedRow = useCallback((row: IWorkerclassExemption) => isUnsavedWageExemptionRow(row), []);
  const { onSearch, filteredRows } = useGridSearch(workerclassExemptions, matches, isUnsavedRow);
  const getRowId = useCallback((row: IWorkerclassExemption) => row.id, []);
  const { pagination, setPagination, revealRow, resetToFirstPage, scrollToRowId } = useGridPagination(
    filteredRows,
    getRowId,
  );

  // A narrower term can leave fewer pages than the one being viewed, and the first match is the one worth
  // showing anyway.
  const onSearchChange = useCallback(
    (value: string) => {
      onSearch(value);
      resetToFirstPage();
    },
    [onSearch, resetToFirstPage],
  );

  useEffect(() => {
    dispatch(getParitairComiteSelectItemsByCompanyAction(toNumber(companyId)));
  }, [companyId, dispatch]);

  const updateWorkerclassExemption = useCallback(
    (id, field, value) => {
      setWorkerclassExemptions(prev => {
        const index = prev.findIndex(w => w.id === id);
        if (index < 0) return prev;
        const updated = { ...prev[index], [field]: value };
        return [...prev.slice(0, index), updated, ...prev.slice(index + 1)];
      });
    },
    [setWorkerclassExemptions],
  );

  const onWageChange = useCallback(
    (id: number, value: number) => updateWorkerclassExemption(id, 'wageExemption', value),
    [updateWorkerclassExemption],
  );

  const onAddNewWorkerclassWageExemption = useCallback(() => {
    const id = Math.random();
    setWorkerclassExemptions(prev => [
      { id, functionId: '', workerclass: '', wageExemption: 0, paritairComiteId: '' },
      ...prev,
    ]);
    revealRow(id);
  }, [setWorkerclassExemptions, revealRow]);

  const onDeleteRow = useCallback(
    (id: number) => {
      setWorkerclassExemptions(prev => prev.filter(c => c.id !== id));
    },
    [setWorkerclassExemptions],
  );

  const getRowSeverity = useCallback(
    (row: IWorkerclassExemption): RowSeverity | undefined => (rowErrors.rowIds.includes(row.id) ? 'error' : undefined),
    [rowErrors],
  );

  const hasFieldError = useCallback(
    (row: IWorkerclassExemption, field: WageExemptionField) =>
      rowErrors.fields.includes(field) && rowErrors.rowIds.includes(row.id),
    [rowErrors],
  );

  const workerclassCellRenderer = useCallback(
    ({ row }: { row: IWorkerclassExemption }) => (
      <UPDropdown
        label=""
        value={row.workerclass ?? ''}
        items={workerclassOptions}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'key')}
        onChange={e => updateWorkerclassExemption(row.id, 'workerclass', e.target.value)}
      />
    ),
    [workerclassOptions, t, userIsCompanyManager, updateWorkerclassExemption, hasFieldError],
  );

  const paritairCellRenderer = useCallback(
    ({ row }: { row: IWorkerclassExemption }) => (
      <UPDropdown
        label=""
        value={row.paritairComiteId ?? ''}
        items={paritairComites ?? []}
        placeholder={t(GENERAL__SELECT)}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'jointCommission')}
        onChange={e => updateWorkerclassExemption(row.id, 'paritairComiteId', e.target.value)}
      />
    ),
    [paritairComites, t, userIsCompanyManager, updateWorkerclassExemption, hasFieldError],
  );

  const wageCellRenderer = useCallback(
    ({ row }: { row: IWorkerclassExemption }) => (
      <WageInputCell
        id={row.id}
        value={row.wageExemption}
        disabled={userIsCompanyManager}
        error={hasFieldError(row, 'wage')}
        onChange={onWageChange}
      />
    ),
    [userIsCompanyManager, onWageChange, hasFieldError],
  );

  const deleteCellRenderer = useCallback(
    ({ row }: { row: IWorkerclassExemption }) => (
      <DeleteActionCell id={row.id} disabled={userIsCompanyManager} onDelete={onDeleteRow} />
    ),
    [userIsCompanyManager, onDeleteRow],
  );

  const columns = useMemo<UPTanStackTableColumn<IWorkerclassExemption>[]>(
    () => [
      { id: 'workerclass', header: t(COMPANY_PAYROLL__WORKERCLASS), size: 250, cell: workerclassCellRenderer },
      { id: 'paritairComiteId', header: t(COMPANY_PAYROLL__PARITAIR_COMITE), size: 220, cell: paritairCellRenderer },
      { id: 'wageExemption', header: t(COMPANY_PAYROLL__WAGE_EXEMPTION), size: 160, cell: wageCellRenderer },
      { id: 'startDate', header: t(WAGE_INDEXATION__VALID_FROM), accessorKey: 'startDate', size: 140 },
      { id: 'endDate', header: t(WAGE_INDEXATION__VALID_TO), accessorKey: 'endDate', size: 140 },
      { id: 'actions', header: '', size: 60, align: 'center', headerAlign: 'center', cell: deleteCellRenderer },
    ],
    [t, workerclassCellRenderer, paritairCellRenderer, wageCellRenderer, deleteCellRenderer],
  );

  return (
    <Box>
      <Box
        sx={{
          marginTop: '32px',
          marginBottom: '16px',
          display: 'flex',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
        }}
      >
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: '16px', marginBottom: '16px' }}>
          <UPButton
            text={`+ ${t(COMPANY_PAYROLL__ADD_NEW_WORKCLASS_EXEMPTION)}`}
            onClick={onAddNewWorkerclassWageExemption}
            disabled={userIsCompanyManager}
          />
          <Box display="flex" alignItems="center">
            <CustomSearchBox onSearch={onSearchChange} />
          </Box>
        </Box>
      </Box>
      <UPTanStackTable
        data={filteredRows}
        columns={columns}
        getRowId={getRowId}
        getRowSeverity={getRowSeverity}
        enableSorting={false}
        pagination={pagination}
        onPaginationChange={setPagination}
        autoResetPageIndex={false}
        scrollToRowId={scrollToRowId}
        emptyMessage={t(DATAGRID__NO_ROWS)}
        sx={{ height: 'auto', '& .MuiTableContainer-root': { maxHeight: '500px' } }}
      />
    </Box>
  );
};
export default WorkerclassExemptionsGrid;
